import React, { useState, useEffect } from 'react';

export const DressUpAdminInfo = ({ isDarkMode, API, adminToken, showNotification }) => {
  const [categories, setCategories] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [helpContent, setHelpContent] = useState(null);
  const [showHelpEditor, setShowHelpEditor] = useState(false);
  
  // Fetch categories and help
  useEffect(() => {
    fetchCategories();
    fetchHelpContent();
  }, []);
  
  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API}/admin/souk-clothes/dressup-categories`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchHelpContent = async () => {
    try {
      console.log('[DressUpAdmin] Fetching help content from:', `${API}/dress-up/help`);
      const res = await fetch(`${API}/dress-up/help`);
      if (res.ok) {
        const data = await res.json();
        console.log('[DressUpAdmin] Help content received:', data);
        setHelpContent(data);
      } else {
        console.error('[DressUpAdmin] Failed to fetch help:', res.status, res.statusText);
      }
    } catch (error) {
      console.error('[DressUpAdmin] Error fetching help content:', error);
    }
  };

  const handleSaveHelpContent = async (content) => {
    try {
      console.log('[DressUpAdmin] Saving help content, length:', content.length);
      const res = await fetch(`${API}/admin/dress-up/help`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({ content })
      });
      
      if (res.ok) {
        const data = await res.json();
        console.log('[DressUpAdmin] Help content saved successfully:', data);
        showNotification('Help content updated!', 'success');
        fetchHelpContent();
        setShowHelpEditor(false);
      } else {
        console.error('[DressUpAdmin] Failed to save help:', res.status, res.statusText);
        const errorText = await res.text();
        console.error('[DressUpAdmin] Error response:', errorText);
        showNotification('Error saving help content', 'error');
      }
    } catch (error) {
      console.error('[DressUpAdmin] Error saving help content:', error);
      showNotification('Error saving help content', 'error');
    }
  };
  
  const handleSaveCategory = async (categoryData) => {
    try {
      const url = editingCategory 
        ? `${API}/admin/souk-clothes/dressup-categories/${editingCategory.id}`
        : `${API}/admin/souk-clothes/dressup-categories`;
      
      const method = editingCategory ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryData)
      });
      
      if (res.ok) {
        showNotification(editingCategory ? 'Category updated!' : 'Category created!', 'success');
        fetchCategories();
        setShowCategoryModal(false);
        setEditingCategory(null);
      }
    } catch (error) {
      console.error('Error saving category:', error);
      showNotification('Error saving category', 'error');
    }
  };
  
  const handleDeleteCategory = async (categoryId) => {
    if (!confirm('Delete this category? Items will not be deleted.')) return;
    
    try {
      const res = await fetch(`${API}/admin/souk-clothes/dressup-categories/${categoryId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        showNotification('Category deleted!', 'success');
        fetchCategories();
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      showNotification('Error deleting category', 'error');
    }
  };

  return (
    <div className={`rounded-lg p-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
      <div className="mb-6">
        <h3 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          👗 Dress-Up Avatar - Learn Darija!
        </h3>
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Manage categories and settings for the dress-up learning game
        </p>
      </div>

      {/* Category Management Section */}
      <div className={`rounded-lg p-6 mb-6 ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-purple-50 border-purple-200'} border-2`}>
        <div className="flex items-center justify-between mb-4">
          <h4 className={`font-bold text-lg ${isDarkMode ? 'text-purple-400' : 'text-purple-800'}`}>
            📂 Dress-Up Categories ({categories.length})
          </h4>
          <button
            onClick={() => {
              setEditingCategory(null);
              setShowCategoryModal(true);
            }}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-bold text-sm"
          >
            + Add Category
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {categories.map(cat => (
            <div key={cat.id} className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{cat.icon}</span>
                  <div>
                    <div className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{cat.name}</div>
                    <div className="text-yellow-500 text-sm">{cat.darija}</div>
                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>ID: {cat.id}</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditingCategory(cat);
                      setShowCategoryModal(true);
                    }}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => handleDeleteCategory(cat.id)}
                    className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Help Content Section */}
      <div className={`rounded-lg p-6 mb-6 ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-green-50 border-green-200'} border-2`}>
        <div className="flex items-center justify-between mb-4">
          <h4 className={`font-bold text-lg ${isDarkMode ? 'text-green-400' : 'text-green-800'}`}>
            ❓ How to Play Dress Up Avatar (Help Content)
          </h4>
          <div className="flex gap-2">
            <button
              onClick={async () => {
                if (confirm('Reset help content to default? This will overwrite current content.')) {
                  try {
                    const res = await fetch(`${API}/admin/dress-up/help/reset`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${adminToken}` }
                    });
                    if (res.ok) {
                      showNotification('Help content reset to default!', 'success');
                      fetchHelpContent();
                    }
                  } catch (error) {
                    console.error('Error resetting help:', error);
                    showNotification('Error resetting help content', 'error');
                  }
                }
              }}
              className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg font-bold text-sm"
            >
              🔄 Reset to Default
            </button>
            <button
              onClick={() => setShowHelpEditor(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-bold text-sm"
            >
              ✏️ Edit Help
            </button>
          </div>
        </div>
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} whitespace-pre-wrap`}>
          {helpContent ? helpContent.content : 'Loading...'}
        </div>
        <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          This content is shown to users when they click the ❓ help button in the Dress Up Avatar game
        </p>
      </div>

      <div className={`rounded-lg p-6 mb-6 ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-blue-50 border-blue-200'} border-2`}>
        <h4 className={`font-bold text-lg mb-3 ${isDarkMode ? 'text-blue-400' : 'text-blue-800'}`}>
          📋 How It Works
        </h4>
        <div className="space-y-3 text-sm">
          <div className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <span className="text-2xl">1️⃣</span>
            <div>
              <strong>Users shop in Souk L7wayej</strong>
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                Users buy clothing items from merchants using Tokens
              </p>
            </div>
          </div>
          <div className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <span className="text-2xl">2️⃣</span>
            <div>
              <strong>Items saved to "Stuff I Own"</strong>
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                Purchased items are stored in user's owned_items array
              </p>
            </div>
          </div>
          <div className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <span className="text-2xl">3️⃣</span>
            <div>
              <strong>Dress-Up Avatar uses owned items</strong>
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                Users can only dress up with clothes they've purchased
              </p>
            </div>
          </div>
          <div className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <span className="text-2xl">4️⃣</span>
            <div>
              <strong>Learn Darija while creating outfits</strong>
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                Clicking items shows Darija name + pronunciation audio
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className={`rounded-lg p-6 mb-6 ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-green-50 border-green-200'} border-2`}>
        <h4 className={`font-bold text-lg mb-3 ${isDarkMode ? 'text-green-400' : 'text-green-800'}`}>
          ✨ Managing Clothing Items
        </h4>
        <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          To add/edit items for the Dress-Up Avatar game, use the <strong>Clothing Items (70+)</strong> section:
        </p>
        <ol className={`space-y-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} list-decimal list-inside`}>
          <li>Go to <strong>"Clothing Items"</strong> tab</li>
          <li>Click <strong>"+ Add Item"</strong> to create new clothing</li>
          <li>Fill in all fields including:
            <ul className="ml-6 mt-1 space-y-1 list-disc list-inside">
              <li><strong>Icon</strong>: Emoji (e.g., 👕) or leave empty if using image</li>
              <li><strong>Image URL</strong>: Link to clothing image (optional)</li>
              <li><strong>Pronunciation</strong>: How to say the Darija name</li>
              <li><strong>Audio URL</strong>: Link to pronunciation audio (optional)</li>
              <li><strong>Layer Type</strong>: top, bottom, shoes, accessories, headwear</li>
              <li><strong>Merchant</strong>: Which merchant sells this item</li>
            </ul>
          </li>
          <li>Save the item</li>
          <li>Item will appear in merchant's inventory AND in Dress-Up Avatar (once purchased)</li>
        </ol>
      </div>

      <div className={`rounded-lg p-6 ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-purple-50 border-purple-200'} border-2`}>
        <h4 className={`font-bold text-lg mb-3 ${isDarkMode ? 'text-purple-400' : 'text-purple-800'}`}>
          🎯 Dress-Up Categories (Automatic)
        </h4>
        <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Categories in the Dress-Up Avatar are automatically created based on clothing items' <strong>layer_type</strong>:
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="text-2xl mb-1">👕</div>
            <div className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Tops</div>
            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Items with layer_type: "top", "base"
            </div>
          </div>
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="text-2xl mb-1">👖</div>
            <div className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Bottoms</div>
            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Items with layer_type: "bottom"
            </div>
          </div>
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="text-2xl mb-1">👟</div>
            <div className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Shoes</div>
            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Items with layer_type: "shoes"
            </div>
          </div>
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="text-2xl mb-1">🎩</div>
            <div className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Accessories</div>
            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Items with layer_type: "accessories", "headwear"
            </div>
          </div>
        </div>
      </div>

      <div className={`mt-6 p-4 rounded-lg ${isDarkMode ? 'bg-yellow-900/30 border-yellow-700' : 'bg-yellow-50 border-yellow-200'} border`}>
        <p className={`text-sm ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
          <strong>💡 Tip:</strong> Make sure to add pronunciation and audio URLs to clothing items so users can learn Darija while playing!
        </p>
      </div>

      {/* Help Content Editor Modal */}
      {showHelpEditor && (
        <HelpEditorModal
          isDarkMode={isDarkMode}
          currentContent={helpContent?.content || ''}
          onSave={handleSaveHelpContent}
          onClose={() => setShowHelpEditor(false)}
        />
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <CategoryModal
          category={editingCategory}
          onSave={handleSaveCategory}
          onClose={() => {
            setShowCategoryModal(false);
            setEditingCategory(null);
          }}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
};

// Category Modal Component
const CategoryModal = ({ category, onSave, onClose, isDarkMode }) => {
  const [formData, setFormData] = useState({
    id: category?.id || '',
    name: category?.name || '',
    icon: category?.icon || '📦',
    darija: category?.darija || '',
    order: category?.order || 999
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.id && !category) {
      alert('Please enter category ID (e.g., tops, bottoms)');
      return;
    }
    if (!formData.name) {
      alert('Please enter category name');
      return;
    }
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className={`rounded-xl p-6 max-w-md w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          {category ? 'Edit Category' : 'Add Category'}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={`block text-sm font-bold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Category ID (lowercase, no spaces) {!category && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={formData.id}
              onChange={(e) => setFormData({ ...formData, id: e.target.value.toLowerCase().replace(/\s/g, '_') })}
              placeholder="e.g., tops, bottoms, shoes"
              disabled={category !== null}
              required={!category}
              className={`w-full p-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'} ${category ? 'opacity-50 cursor-not-allowed' : ''}`}
            />
            {category && (
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Category ID cannot be changed after creation
              </p>
            )}
            {!category && (
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                This will be used in your code/database. Examples: tops, bottoms, dresses, shoes
              </p>
            )}
          </div>

          <div>
            <label className={`block text-sm font-bold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Category Name (English)
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Tops"
              className={`w-full p-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            />
          </div>

          <div>
            <label className={`block text-sm font-bold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Icon (Emoji)
            </label>
            <input
              type="text"
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              placeholder="👕"
              className={`w-full p-2 rounded border text-3xl ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            />
          </div>

          <div>
            <label className={`block text-sm font-bold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Darija Name (Arabic)
            </label>
            <input
              type="text"
              value={formData.darija}
              onChange={(e) => setFormData({ ...formData, darija: e.target.value })}
              placeholder="فوقاني"
              className={`w-full p-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            />
          </div>

          <div>
            <label className={`block text-sm font-bold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Order (for sorting)
            </label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
              className={`w-full p-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            />
          </div>

          <div className="flex gap-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-4 py-2 rounded font-bold ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-800'}`}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded font-bold"
            >
              {category ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Help Editor Modal Component
const HelpEditorModal = ({ isDarkMode, currentContent, onSave, onClose }) => {
  const [content, setContent] = useState(currentContent);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(content);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          ❓ Edit "How to Play Dress Up Avatar" Help Content
        </h3>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className={`block text-sm font-bold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Help Content (supports line breaks)
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={20}
              placeholder="Enter help instructions here...&#10;&#10;Example:&#10;Welcome to Dress Up Avatar!&#10;&#10;1. Click clothing items to add them to canvas&#10;2. Drag items to move them&#10;3. Click 🎨 Colors to adjust colors"
              className={`w-full p-3 rounded border font-mono text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            />
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              This text will be displayed in the help modal when users click ❓ in the game
            </p>
          </div>

          <div className="flex gap-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-4 py-2 rounded font-bold ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-800'}`}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-bold"
            >
              💾 Save Help Content
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
